import concat from 'lodash/concat';
import forEach from 'lodash/forEach';
import noop from 'lodash/noop';
import uniqueId from 'lodash/uniqueId';
import { appendScriptPromise, objectFilter } from '../../../../../../shared/utils/commonUtils';
import pubSubInstance from '../../../../../../shared/utils/pubSub';
import { googleAddressParse } from './addressParse';
import { emailParse } from './emailParse';
import { WIDGETGLOBALNAME } from '../../../../../../shared/config/constant';

const PRODENVCOLLECTION = [ 'lv-prodcw', 'lv-prod', 'lv-dr' ];
const DISPALYITEMSMAP = {
  subTotal: 'checkout.lib.components.orderDetailContent.subTotal',
  yourPayment: 'checkout.lib.components.text.orderTotal',
  processingFee: 'checkout.lib.components.text.serviceFee',
  paymentProcessingFee: 'checkout.lib.components.text.totalPPF',
  aaDiscount: 'checkout.lib.components.text.aaDiscount',
};

const GOOGLEPAYURL = 'https://pay.google.com/gp/p/js/pay.js';
const BASEREQUESTCONFIG = {
  apiVersion: 2,
  apiVersionMinor: 0,
};
const ALLOWEDCARDAUTHMETHODS = [ 'PAN_ONLY', 'CRYPTOGRAM_3DS' ];

const nativeUserAgentPatternForAndroid = /(?=.*captivate)(?=.*android)/i;

const GOOGLEPAYMENTCLIENTFORANDROID = 'activeGooglePaymentClientForAndroid'; // The global object exposed by the Android integrator on the window for widgets to call
// The object exposed on the widget global object for the implementation of google pay on Android,
// includes the payment process function entry and a cache of actual payment process functions
const GOOGLEFORANDROID = 'googleForAndroid';
const PROCESSORSNAME = 'processors'; // A map type cache of actual process payment functions.
const PAYMENTCALLBACKNAME = 'processPayment'; // A payment process function entry for payment of Google Pay on Android
const READYTOPAYCALLBACKNAME = 'readyToPayCallback'; // A callback entry to notify the widget Google Pay if it is ready

const paymentCallbackEntryForAndroid = errorHandler => ({ additionalParams, ...rest }) => {
  const processor = window[WIDGETGLOBALNAME][GOOGLEFORANDROID][PROCESSORSNAME].get(additionalParams?.processorId);
  processor
    ? processor.paymentCallback(rest)
    : errorHandler('Google pay loadPayData error! processorId is not match');
};

const readyToPayCallbackEntryForAndroid = ({ additionalParams, ...rest }) => {
  const processor = window[WIDGETGLOBALNAME][GOOGLEFORANDROID][PROCESSORSNAME]
    .get(additionalParams?.processorId);
  processor && processor.readyToPayCallback(rest);
};
class GooglePayment {
  commitCallback = noop;

  commitErrorMsg = undefined;

  logTrack = noop;

  createGooglePayAccount = undefined;

  paymentsClient = undefined;

  isAndroid = nativeUserAgentPatternForAndroid.test(navigator.userAgent);

  processorId = uniqueId(`${Date.now()}_`);

  buildBaseCardPaymentMethod = ({ collectBillingInfo = false, googlePayInfo }) => {
    const { supportCardTypes } = googlePayInfo;
    const params = {
      allowedAuthMethods: ALLOWEDCARDAUTHMETHODS,
      allowedCardNetworks: supportCardTypes,
    };
    return {
      type: 'CARD',
      parameters: collectBillingInfo ? {
        ...params,
        billingAddressRequired: true,
        billingAddressParameters: {
          format: 'FULL',
        },
      } : params,
    };
  };

  buildGooglePaymentDataRequest = (initParams) => {
    const {
      currencyCode, i18n, collectBillingInfo, collectEmail = false,
      yourPayment, paymentCountryCode, subItems = {},
      googlePayInfo,
    } = initParams;

    const {
      merchantId,
      merchantName,
      publicKey,
    } = googlePayInfo;
    const allowedAuthMethods = {
      ...this.buildBaseCardPaymentMethod({ collectBillingInfo, googlePayInfo }),
      tokenizationSpecification: {
        type: 'DIRECT',
        parameters: {
          protocolVersion: 'ECv2',
          publicKey,
        },
      },
    };

    const items = objectFilter(subItems);
    let displayItems = [];
    forEach(items, (v, k) => {
      if (v instanceof Array) {
        displayItems = concat(displayItems, v.map(item => ({
          label: item.name,
          type: 'LINE_ITEM',
          price: item.amount.toString(),
        })));
      } else {
        displayItems = concat(displayItems, [{
          label: i18n(DISPALYITEMSMAP[k]),
          type: k === 'subTotal' ? 'SUBTOTAL' : 'LINE_ITEM',
          price: v.toString(),
        }]);
      }
    });
    const googlePayTransactionInfo = {
      countryCode: paymentCountryCode,
      currencyCode,
      totalPriceStatus: 'FINAL',
      totalPriceLabel: i18n(DISPALYITEMSMAP.yourPayment),
      totalPrice: yourPayment.toString(),
      displayItems,
    };

    return {
      ...BASEREQUESTCONFIG,
      allowedPaymentMethods: [ allowedAuthMethods ],
      transactionInfo: googlePayTransactionInfo,
      merchantInfo: {
        merchantId,
        merchantName,
      },
      callbackIntents: [ 'PAYMENT_AUTHORIZATION' ],
      emailRequired: collectEmail,
    };
  };

  processPayment = initParams => ({
    paymentData, resolveCallback = noop,
  }) => {
    const { googlePayInfo, i18n, enablePreviewMode } = initParams;
    const { sessionId } = googlePayInfo;
    const {
      paymentMethodData: {
        tokenizationData: { token },
        info: { cardDetails, billingAddress },
      }, email,
    } = paymentData;
    const params = {
      SessionID: sessionId,
      GooglePaySealedMessage: token,
      LastFourDigits: cardDetails,
    };
    const billingContact = googleAddressParse({ ...billingAddress, email });
    const receiptEmail = emailParse(email);
    if (enablePreviewMode) {
      resolveCallback({ transactionState: 'SUCCESS' });
      this.commitCallback?.({ status: true });
      return;
    }
    this.createGooglePayAccount?.({ params, collectHeaderToken: false }).unwrap().then((rsp) => {
      resolveCallback({ transactionState: 'SUCCESS' });
      if (rsp === 'SUCCESS') {
        const data = {
          status: true, paymentAccountId: sessionId, paymentMethodType: 'GOOGLEPAY', billingContact, receiptEmail,
        };
        this.commitCallback(objectFilter(data));
      } else {
        const msg = i18n('checkout.lib.errorCode.googlePay');
        this.commitCallback({ status: false, msg });
        this.logTrack({
          params: {
            message: `create google pay acount failed: ${msg}`,
          },
        });
      }
    }).catch(() => {
      this.commitCallback({ status: false });
    });
  };

 initGooglePaymentsClient = (initParams) => {
   const { googlePayInfo } = initParams;
   const { environment } = googlePayInfo;
  if (window.google) { // eslint-disable-line
    this.paymentsClient = new google.payments.api.PaymentsClient({ // eslint-disable-line
       environment: PRODENVCOLLECTION.includes(environment) ? 'PRODUCTION' : 'TEST',
       paymentDataCallbacks: {
         onPaymentAuthorized: (paymentData) => {
           return new Promise((resolve) => {
             this.processPayment(initParams)({
               paymentData, resolveCallback: resolve,
             });
           });
         },
       },
     });
   }
 };

 onGooglePayLoaded = async (initParams) => {
   this.initGooglePaymentsClient(initParams);
   const { collectBillingInfo, googlePayInfo } = initParams;
   const baseCardPaymentMethod = this.buildBaseCardPaymentMethod({ collectBillingInfo, googlePayInfo });
   const googlePayIsReadyToPayRequest = {
     ...BASEREQUESTCONFIG,
     allowedPaymentMethods: [ baseCardPaymentMethod ],
   };
   if (this.isAndroid) {
     const { environment } = googlePayInfo;
     const res = await new Promise((resolve) => {
       if (window[GOOGLEPAYMENTCLIENTFORANDROID]) {
         this.registerProcessGooglePaymentForAndroid(initParams, resolve);
         window[GOOGLEPAYMENTCLIENTFORANDROID].isReadyToPay(
           JSON.stringify(googlePayIsReadyToPayRequest),
           PRODENVCOLLECTION.includes(environment),
           JSON.stringify({
             processorId: this.processorId,
           }),
          `window.${WIDGETGLOBALNAME}.${GOOGLEFORANDROID}.${READYTOPAYCALLBACKNAME}`,
         );
       } else {
         resolve(false);
       }
     });
     return res;
   }
   if (!this.paymentsClient) {
     return false;
   }
   try {
     const res = await this.paymentsClient?.isReadyToPay(googlePayIsReadyToPayRequest);
     return !!res.result;
   } catch {
     return false;
   }
 };

 loadGoogleScript = async () => {
   if (window.google) return true;
   try {
     const loadScriptSuccess = await appendScriptPromise(GOOGLEPAYURL);
     return loadScriptSuccess;
   } catch {
     return false;
   }
 };

 checkGooglePaySupport = async (initParams) => {
   const { paymentCountryCode, googlePayInfo } = initParams;

   if (!paymentCountryCode) {
     return false;
   }

   if (!googlePayInfo) {
     return false;
   }

   const loadScriptState = await this.loadGoogleScript();
   if (!loadScriptState) {
     return false;
   }

   const checkReadyState = await this.onGooglePayLoaded(initParams);

   return checkReadyState;
 };

 createGooglePayButton = ({ widgetNameSpace, buttonColor = 'blank' }) => {
   const containerClassName = `${widgetNameSpace}-google-customize-btn`;
   const btnContainerList = document.getElementsByClassName(containerClassName);
   [ ...btnContainerList ].forEach((container) => {
     container.firstChild && container.removeChild(container.firstChild);
     const btn = this.paymentsClient?.createButton({
       onClick: () => { pubSubInstance.dispatch(`${widgetNameSpace}-submit-order`); },
       buttonColor,
       buttonType: 'plain',
       buttonSizeMode: 'fill',
       buttonRadius: 6,
     });
     return btn && container.appendChild(btn);
   });
 };

 handleLoadPaymentDataError = (logMsg = 'Google pay loadPayData error!') => {
   this.commitCallback({ status: false, msg: this.commitErrorMsg });
   this.logTrack({
     params: {
       message: logMsg,
     },
   });
   throw new Error(logMsg);
 };

 loadPaymentDataForWeb = ({ paymentDataRequest }) => {
   try {
     this.paymentsClient?.loadPaymentData(paymentDataRequest).catch(() => {
       this.commitCallback({ status: false });
     });
   } catch {
     this.handleLoadPaymentDataError();
   }
 }

 onGooglePaymentButtonClicked = ({
   params, commitCallback, createGooglePayAccount, logTrack = noop,
 }) => {
   this.commitCallback = commitCallback;
   this.createGooglePayAccount = createGooglePayAccount;
   this.logTrack = logTrack;
   const { i18n } = params;
   this.commitErrorMsg = i18n('checkout.lib.errorCode.googlePay');
   const paymentDataRequest = this.buildGooglePaymentDataRequest(params);
   if (this.isAndroid) {
     this.loadPaymentDataForAndroid({ paymentDataRequest });
   } else {
     this.loadPaymentDataForWeb({ paymentDataRequest });
   }
 };

 processPaymentForAndroid = initParams => ({ statusCode, paymentData }) => {
   if (statusCode === 'SUCCESS') {
     this.processPayment(initParams)({ paymentData });
   } else if (statusCode === 'CANCELED') {
     this.commitCallback({ status: false });
   } else {
     this.handleLoadPaymentDataError();
   }
 };

 processReadyToPayForAndroid = resolve => ({ isReadyToPay }) => {
   resolve(!!isReadyToPay);
 }

  registerProcessGooglePaymentForAndroid = (initParams, resolve) => {
    if (!window[WIDGETGLOBALNAME][GOOGLEFORANDROID]) {
      window[WIDGETGLOBALNAME][GOOGLEFORANDROID] = {
        [PAYMENTCALLBACKNAME]: paymentCallbackEntryForAndroid(this.handleLoadPaymentDataError),
        [READYTOPAYCALLBACKNAME]: readyToPayCallbackEntryForAndroid,
        [PROCESSORSNAME]: new Map(),
      };
    }
    window[WIDGETGLOBALNAME][GOOGLEFORANDROID][PROCESSORSNAME].set(
      this.processorId, {
        paymentCallback: this.processPaymentForAndroid(initParams),
        readyToPayCallback: this.processReadyToPayForAndroid(resolve),
      },
    );
  };

 loadPaymentDataForAndroid = ({ paymentDataRequest }) => {
   if (window[GOOGLEPAYMENTCLIENTFORANDROID]) {
     window[GOOGLEPAYMENTCLIENTFORANDROID].loadPaymentData(
       JSON.stringify(paymentDataRequest),
      `window.${WIDGETGLOBALNAME}.${GOOGLEFORANDROID}.${PAYMENTCALLBACKNAME}`,
     );
   } else {
     this.handleLoadPaymentDataError('Google pay loadPayData error! activeGooglePaymentClientForAndroid is undefined');
   }
 };
}

export default GooglePayment;
